import { useEffect, useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

// If the new service worker never takes control (and so the page never reloads), unlock the buttons again.
const UPDATE_TIMEOUT_MS = 15_000;

function checkForUpdate(reg: ServiceWorkerRegistration) {
  // Fails offline or while a worker is being installed; the next check will retry.
  reg.update().catch(() => {});
}

export function UpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, reg) {
      // Check right away on launch (also in the installed PWA), hourly and whenever the app comes to the foreground.
      if (!reg) return;
      checkForUpdate(reg);
      setInterval(() => checkForUpdate(reg), 60 * 60 * 1000);
      document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && checkForUpdate(reg));
    },
  });
  const [updating, setUpdating] = useState(false);
  const updateBtn = useRef<HTMLButtonElement>(null);

  // "Later" hides the prompt until the next launch; the new worker keeps waiting and is offered again then.
  const later = () => setNeedRefresh(false);

  const update = () => {
    setUpdating(true);
    // Activates the waiting worker; the page reloads once it takes control.
    updateServiceWorker(true).catch(() => setUpdating(false));
  };

  useEffect(() => {
    if (!updating) return;
    const t = setTimeout(() => setUpdating(false), UPDATE_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [updating]);

  useEffect(() => {
    if (!needRefresh) return;
    // Taking focus from a text field would close the on-screen keyboard mid-typing.
    const el = document.activeElement as HTMLElement | null;
    if (el && (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT' || el.isContentEditable)) return;
    updateBtn.current?.focus();
  }, [needRefresh]);

  useEffect(() => {
    if (!needRefresh || updating) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setNeedRefresh(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [needRefresh, updating, setNeedRefresh]);

  if (!needRefresh) return null;
  // No onClick on the backdrop: a stray tap must not postpone the update.
  return (
    <div className="modal-backdrop update-prompt-backdrop">
      <div className="modal update-prompt" role="dialog" aria-modal="true" aria-labelledby="update-prompt-title">
        <h2 id="update-prompt-title">Доступно обновление</h2>
        <p className="muted">Вышла новая версия Evo. Обновите сейчас, чтобы получить последние изменения.</p>
        <div className="update-actions">
          <button ref={updateBtn} className="btn primary" onClick={update} disabled={updating}>
            {updating ? 'Обновляем…' : 'Обновить сейчас'}
          </button>
          <button className="btn ghost" onClick={later} disabled={updating}>
            Позже
          </button>
        </div>
      </div>
    </div>
  );
}
